import React from "react";
import { Clock, MapPin, Banknote, CreditCard, ArrowUpRight } from "lucide-react";
import { pickupRangeLabel } from "../lib/pickup";
import { restaurantMapsUrl } from "../lib/maps";
import { scheduleLine, DAY_KEYS, dayName, dayHoursLabel, todayKey } from "../lib/hours";

// Highlighted pickup-time pill. Colour reflects the current preparation range:
// 10-15 green, 15-20 orange, 20+ red.
const RANGE_STYLE = {
  "10_15": "text-brand-green bg-brand-green/10",
  "15_20": "text-brand-orange bg-brand-orange/10",
  "20_30": "text-brand-red bg-brand-red/10",
  "30_PLUS": "text-brand-red bg-brand-red/10",
};

export function PickupTimeBadge({ range, size = 15 }) {
  const cls = RANGE_STYLE[range] || RANGE_STYLE["15_20"];
  return (
    <span data-testid="pickup-badge" data-range={range}
      className={`inline-flex items-center gap-1.5 font-display font-black px-3 py-1.5 rounded-full text-sm w-fit ${cls}`}>
      <Clock size={size} strokeWidth={2.5} /> {pickupRangeLabel(range)}
    </span>
  );
}

// Payment method, immediately understandable. Never shows unsupported methods.
export function PaymentMethodDisplay({ setting = "BOTH", locale = "sr", size = 16 }) {
  const s = (setting || "BOTH").toUpperCase();
  const cash = locale === "en" ? "Cash" : "Gotovina";
  const card = locale === "en" ? "Card" : "Kartica";
  const aria = locale === "en" ? "Accepted payment methods" : "Prihvaćeni načini plaćanja";
  const base = "inline-flex items-center gap-1.5 text-slate-700 font-semibold text-sm";
  if (s === "CASH") {
    return (
      <span data-testid="payment-display" role="group" aria-label={aria} className={base}>
        <Banknote size={size} strokeWidth={2} className="text-brand-green" aria-hidden="true" />
        {locale === "en" ? "Cash at pickup" : "Gotovina pri preuzimanju"}
      </span>
    );
  }
  if (s === "CARD") {
    return (
      <span data-testid="payment-display" role="group" aria-label={aria} className={base}>
        <CreditCard size={size} strokeWidth={2} className="text-slate-500" aria-hidden="true" />
        {locale === "en" ? "Card at restaurant" : "Kartica u restoranu"}
      </span>
    );
  }
  return (
    <span data-testid="payment-display" role="group" aria-label={aria} className={base}>
      <Banknote size={size} strokeWidth={2} className="text-brand-green" aria-hidden="true" /> {cash}
      <span className="text-slate-300" aria-hidden="true">·</span>
      <CreditCard size={size} strokeWidth={2} className="text-slate-500" aria-hidden="true" /> {card}
    </span>
  );
}

// Clickable address row — opens the exact restaurant location in Google Maps.
// stopPropagation keeps a parent card from also navigating into the menu.
export function RestaurantAddressLink({ restaurant, locale = "sr", size = 16, className = "" }) {
  const url = restaurantMapsUrl(restaurant);
  const label = (restaurant?.address || restaurant?.city || "").trim();
  if (!url || !label) return null;
  const aria = locale === "en" ? "Open restaurant location in Google Maps" : "Otvori lokaciju restorana u Google Maps";
  return (
    <a href={url} target="_blank" rel="noopener noreferrer" aria-label={aria}
      data-testid="restaurant-address-link" onClick={(e) => e.stopPropagation()}
      className={`inline-flex items-center gap-1.5 text-slate-700 font-semibold text-sm hover:text-brand-red transition-colors ${className}`}>
      <MapPin size={size} strokeWidth={2} className="text-brand-red flex-shrink-0" aria-hidden="true" />
      <span className="truncate">{label}</span>
      <ArrowUpRight size={size - 3} strokeWidth={2.5} className="text-slate-400 flex-shrink-0" aria-hidden="true" />
    </a>
  );
}

// Contextual opening-hours line (server-authoritative state, locale-formatted text).
export function OpeningStatusLine({ restaurant, locale = "sr", t = (k, d) => d, size = 16, className = "" }) {
  const line = scheduleLine(restaurant?.schedule, t, locale);
  if (!line) return null;
  const open = restaurant.schedule.state === "open";
  return (
    <span data-testid="opening-status-line"
      className={`inline-flex items-center gap-1.5 text-sm font-semibold ${open ? "text-slate-700" : "text-slate-500"} ${className}`}>
      <Clock size={size} strokeWidth={2} className={open ? "text-brand-green" : "text-slate-400"} aria-hidden="true" />
      {line}
    </span>
  );
}

// Reusable block: pickup time (strongest) + payment + clickable address.
export function RestaurantOperationalInfo({ restaurant, locale = "sr", t = (k, d) => d, className = "" }) {
  if (!restaurant) return null;
  return (
    <div data-testid="restaurant-operational-info" className={`flex flex-col gap-2 ${className}`}>
      <PickupTimeBadge range={restaurant.effective_pickup_range} />
      <OpeningStatusLine restaurant={restaurant} locale={locale} t={t} />
      <PaymentMethodDisplay setting={restaurant.payment_method} locale={locale} />
      <RestaurantAddressLink restaurant={restaurant} locale={locale} />
    </div>
  );
}

// Full weekly schedule for the restaurant information section, today highlighted.
export function WeeklyHours({ restaurant, locale = "sr", t = (k, d) => d, className = "" }) {
  const hours = restaurant?.opening_hours || {};
  if (!Object.keys(hours).length) return null;
  const today = todayKey();
  return (
    <div data-testid="weekly-hours" className={className}>
      <h3 className="font-display font-black text-sm uppercase tracking-wider text-slate-400 mb-2">
        {t("hours.title", "Radno vreme")}
      </h3>
      <OpeningStatusLine restaurant={restaurant} locale={locale} t={t} className="mb-2" />
      <ul className="divide-y divide-slate-100">
        {DAY_KEYS.map((d) => (
          <li key={d} data-testid={`hours-row-${d}`}
            className={`flex items-center justify-between py-1.5 text-sm ${d === today ? "font-bold text-brand-red" : "text-slate-600"}`}>
            <span>{dayName(d, locale)}</span>
            <span>{dayHoursLabel(hours[d], locale)}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
